import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import DossierStatusBadge from './DossierStatusBadge';
import DocumentVault from './DocumentVault';
import { updateDossierStatus, validateDossierConformity } from '../../../services/notairePortalService';

const formatCurrency = (n) =>
  n ? new Intl.NumberFormat('fr-CI', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 })?.format(n) : '—';

const formatDate = (d) =>
  d ? new Date(d)?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const WORKFLOW_STEPS = [
  { status: 'nouveau', label: 'Nouveau dossier', icon: 'Clock', desc: 'En attente d\'acceptation' },
  { status: 'instruction_en_cours', label: 'Instruction', icon: 'Search', desc: 'Vérification des pièces' },
  { status: 'dossier_valide', label: 'Dossier Validé', icon: 'CheckCircle', desc: 'Conforme' },
  { status: 'projet_acte_depose', label: "Projet d\'acte", icon: 'FileCheck', desc: 'Acte déposé' },
  { status: 'transaction_cloturee', label: 'Clôturée', icon: 'CheckSquare', desc: 'Transaction finalisée' },
];

const STATUS_ORDER = ['nouveau', 'instruction_en_cours', 'dossier_valide', 'projet_acte_depose', 'transaction_cloturee'];

const DossierDetail = ({ dossier, onDossierUpdated, notaireProfile = null }) => {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  const currentIndex = STATUS_ORDER?.indexOf(dossier?.status);

  const handleAction = async (action) => {
    setUpdating(true);
    setError('');
    let result;
    switch (action) {
      case 'accept':
        result = await updateDossierStatus(dossier?.id, 'instruction_en_cours');
        break;
      case 'validate':
        result = await validateDossierConformity(dossier?.id);
        break;
      case 'deposit_acte':
        result = await updateDossierStatus(dossier?.id, 'projet_acte_depose');
        break;
      case 'close':
        result = await updateDossierStatus(dossier?.id, 'transaction_cloturee');
        break;
      default:
        break;
    }
    if (result?.error) setError(result?.error);
    else onDossierUpdated?.();
    setUpdating(false);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-border/50 p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-ios-caption1 font-system font-body-semibold text-brand-earth bg-brand-gold/10 px-2 py-0.5 rounded-lg">
                {dossier?.reference}
              </span>
              {dossier?.is_verified_badge && (
                <span className="flex items-center space-x-1 text-[10px] font-system font-body-semibold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
                  <Icon name="BadgeCheck" size={10} />
                  <span>Vérifié par Notaire</span>
                </span>
              )}
            </div>
            <h2 className="text-ios-title3 font-display font-display-semibold text-text-primary">
              {dossier?.property_title || 'Bien non renseigné'}
            </h2>
            <p className="text-ios-callout text-text-secondary mt-0.5">{dossier?.property_address || '—'}</p>
          </div>
          <DossierStatusBadge status={dossier?.status} />
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'Montant', value: formatCurrency(dossier?.transaction_amount), icon: 'Banknote' },
            { label: 'Créé le', value: formatDate(dossier?.created_at), icon: 'Calendar' },
            { label: 'Acheteur', value: dossier?.buyer?.full_name || '—', icon: 'User' },
            { label: 'Vendeur', value: dossier?.seller?.full_name || '—', icon: 'User' },
          ]?.map(item => (
            <div key={item?.label} className="bg-surface/50 rounded-xl p-3">
              <div className="flex items-center space-x-1.5 mb-1">
                <Icon name={item?.icon} size={12} className="text-text-secondary" />
                <p className="text-[11px] font-system text-text-secondary uppercase tracking-wider">{item?.label}</p>
              </div>
              <p className="text-ios-callout font-system font-body-semibold text-text-primary truncate">{item?.value}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Workflow progress */}
      <div className="bg-white rounded-2xl border border-border/50 p-5">
        <h3 className="text-ios-callout font-system font-body-semibold text-text-primary mb-4">Progression du dossier</h3>
        <div className="flex items-center gap-1">
          {WORKFLOW_STEPS?.map((step, idx) => {
            const isDone = idx <= currentIndex;
            const isCurrent = idx === currentIndex;
            return (
              <React.Fragment key={step?.status}>
                <div className={`flex flex-col items-center flex-1 min-w-0 ${isCurrent ? 'opacity-100' : isDone ? 'opacity-80' : 'opacity-30'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1.5 ${
                    isCurrent ? 'bg-brand-earth text-white shadow-ios-light ring-2 ring-brand-earth/30' :
                    isDone ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-400'
                  }`}>
                    <Icon name={isDone && !isCurrent ? 'Check' : step?.icon} size={14} />
                  </div>
                  <p className={`text-[10px] font-system font-body-semibold text-center leading-tight ${isCurrent ? 'text-brand-earth' : isDone ? 'text-green-600' : 'text-text-secondary'}`}>
                    {step?.label}
                  </p>
                </div>
                {idx < WORKFLOW_STEPS?.length - 1 && (
                  <div className={`h-0.5 flex-1 max-w-6 rounded-full ${idx < currentIndex ? 'bg-green-400' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      {/* Action buttons */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-100 rounded-xl">
          <Icon name="AlertCircle" size={14} className="text-red-500" />
          <p className="text-ios-caption1 text-red-600 font-system">{error}</p>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-border/50 p-5 space-y-3">
        <h3 className="text-ios-callout font-system font-body-semibold text-text-primary">Actions disponibles</h3>

        {dossier?.status === 'nouveau' && (
          <button
            onClick={() => handleAction('accept')}
            disabled={updating}
            className="w-full flex items-center justify-center space-x-2 py-3 bg-blue-600 text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-blue-700 disabled:opacity-60 transition-colors">
            <Icon name="FolderOpen" size={16} />
            <span>{updating ? 'Traitement...' : 'Accepter le dossier'}</span>
          </button>
        )}

        {dossier?.status === 'instruction_en_cours' && (
          <button
            onClick={() => handleAction('validate')}
            disabled={updating}
            className="w-full flex items-center justify-center space-x-2 py-3 bg-green-600 text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-green-700 disabled:opacity-60 transition-colors">
            <Icon name="BadgeCheck" size={16} />
            <span>{updating ? 'Traitement...' : 'Valider la conformité du bien'}</span>
          </button>
        )}

        {dossier?.status === 'dossier_valide' && (
          <button
            onClick={() => handleAction('deposit_acte')}
            disabled={updating}
            className="w-full flex items-center justify-center space-x-2 py-3 bg-purple-600 text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-purple-700 disabled:opacity-60 transition-colors">
            <Icon name="FileCheck" size={16} />
            <span>{updating ? 'Traitement...' : "Déposer le projet d'acte"}</span>
          </button>
        )}

        {dossier?.status === 'projet_acte_depose' && (
          <button
            onClick={() => handleAction('close')}
            disabled={updating}
            className="w-full flex items-center justify-center space-x-2 py-3 bg-brand-earth text-white rounded-xl text-ios-callout font-system font-body-semibold hover:bg-brand-earth/90 disabled:opacity-60 transition-colors">
            <Icon name="CheckSquare" size={16} />
            <span>{updating ? 'Traitement...' : 'Finaliser la transaction'}</span>
          </button>
        )}

        {dossier?.status === 'transaction_cloturee' && (
          <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-xl">
            <Icon name="CheckCircle" size={16} className="text-green-600" />
            <p className="text-ios-callout font-system font-body-semibold text-green-700">Transaction clôturée et archivée</p>
          </div>
        )}
      </div>
      {/* Document vault */}
      <div className="bg-white rounded-2xl border border-border/50 p-5">
        <DocumentVault
          dossierId={dossier?.id}
          onDocumentUpdated={onDossierUpdated}
          isNotaire={true}
          notaireProfile={notaireProfile}
        />
      </div>
    </div>
  );
};

export default DossierDetail;
